import type { Attendee, LinkedCalendar, PlannerEvent } from '../types/family-planner'
import { getAccessToken, type OAuthProvider } from './planner-oauth'

// ─── Provider reads (browser → Google / Microsoft directly) ──
// Both APIs are CORS-enabled for browser clients holding a bearer token, so the
// calendar list and events are fetched straight from the provider. Everything is
// normalized into the planner's own event shape before it reaches the UI.

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444', '#6366f1']

function colorFor(id: string, index: number): string {
  return PALETTE[index % PALETTE.length] || PALETTE[id.length % PALETTE.length]
}

/** Naive local 'YYYY-MM-DDTHH:MM' — the planner stores wall-clock times. */
function toLocalStamp(date: Date): string {
  const pad = (n: number) => `${n}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function windowBounds(daysBack: number, daysForward: number): { start: Date; end: Date } {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  start.setDate(start.getDate() - daysBack)
  const end = new Date()
  end.setHours(23, 59, 59, 0)
  end.setDate(end.getDate() + daysForward)
  return { start, end }
}

async function authedFetch(provider: OAuthProvider, url: string, extraHeaders: Record<string, string> = {}) {
  const token = await getAccessToken(provider)
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const body = (await response.json()) as { error?: { message?: string } | string }
      detail =
        typeof body.error === 'string' ? body.error : body.error?.message ? body.error.message : ''
    } catch {
      // non-JSON error body
    }
    if (response.status === 401 || response.status === 403) {
      throw new Error(
        detail ||
          `${provider === 'google' ? 'Google' : 'Microsoft'} refused the request — the link may need to be reconnected.`,
      )
    }
    throw new Error(detail || `${provider} request failed (${response.status}).`)
  }
  return response
}

// ─── Google Calendar ─────────────────────────────────────────

interface GoogleCalendarListEntry {
  id: string
  summary?: string
  summaryOverride?: string
  backgroundColor?: string
  accessRole?: string
  selected?: boolean
  primary?: boolean
}

interface GoogleEvent {
  id: string
  status?: string
  summary?: string
  description?: string
  location?: string
  start?: { date?: string; dateTime?: string }
  end?: { date?: string; dateTime?: string }
  organizer?: { email?: string; displayName?: string }
  attendees?: { email?: string; displayName?: string; responseStatus?: string }[]
  updated?: string
}

const RESPONSE_MAP: Record<string, Attendee['response']> = {
  accepted: 'accepted',
  declined: 'declined',
  tentative: 'tentative',
  needsAction: 'needs-action',
}

async function fetchGoogleCalendars(accountEmail: string): Promise<LinkedCalendar[]> {
  const response = await authedFetch(
    'google',
    'https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=250&minAccessRole=reader',
  )
  const body = (await response.json()) as { items?: GoogleCalendarListEntry[] }
  return (body.items ?? []).map((item, index) => ({
    id: `google:${item.id}`,
    name: item.summaryOverride || item.summary || item.id,
    provider: 'google' as const,
    accountEmail,
    color: item.backgroundColor || colorFor(item.id, index),
    // Respect what the owner already unchecked in Google Calendar.
    enabled: item.selected !== false,
    readOnly: item.accessRole !== 'owner' && item.accessRole !== 'writer',
    memberId: null,
  }))
}

async function fetchGoogleEvents(
  calendars: LinkedCalendar[],
  daysBack: number,
  daysForward: number,
): Promise<PlannerEvent[]> {
  const { start, end } = windowBounds(daysBack, daysForward)
  const events: PlannerEvent[] = []

  for (const calendar of calendars) {
    const providerId = calendar.id.slice('google:'.length)
    const params = new URLSearchParams({
      timeMin: start.toISOString(),
      timeMax: end.toISOString(),
      singleEvents: 'true', // expand recurrences into individual dates
      orderBy: 'startTime',
      maxResults: '250',
    })
    const response = await authedFetch(
      'google',
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(providerId)}/events?${params}`,
    )
    const body = (await response.json()) as { items?: GoogleEvent[] }
    for (const item of body.items ?? []) {
      if (item.status === 'cancelled') continue
      const allDay = !!item.start?.date
      const startValue = item.start?.dateTime ?? item.start?.date
      const endValue = item.end?.dateTime ?? item.end?.date
      if (!startValue) continue
      events.push({
        id: `google:${providerId}:${item.id}`,
        title: item.summary?.trim() || '(no title)',
        // Google all-day ends are already exclusive, matching eventDaySpan().
        start: allDay ? startValue : toLocalStamp(new Date(startValue)),
        end: allDay
          ? endValue ?? startValue
          : toLocalStamp(new Date(endValue ?? startValue)),
        allDay,
        location: item.location,
        notes: item.description,
        calendarId: calendar.id,
        origin: 'calendar',
        originRef: item.id,
        organizer: item.organizer?.email || item.organizer?.displayName,
        attendees: (item.attendees ?? []).map<Attendee>((a) => ({
          name: a.displayName || a.email || 'Guest',
          email: a.email,
          response: a.responseStatus ? RESPONSE_MAP[a.responseStatus] : undefined,
        })),
        memberIds: calendar.memberId ? [calendar.memberId] : [],
        category: 'other',
        updatedAt: item.updated ?? new Date().toISOString(),
      })
    }
  }
  return events
}

// ─── Microsoft Graph ─────────────────────────────────────────

interface GraphCalendar {
  id: string
  name?: string
  hexColor?: string
  canEdit?: boolean
  isDefaultCalendar?: boolean
}

interface GraphEvent {
  id: string
  subject?: string
  bodyPreview?: string
  isAllDay?: boolean
  isCancelled?: boolean
  start?: { dateTime?: string; timeZone?: string }
  end?: { dateTime?: string; timeZone?: string }
  location?: { displayName?: string }
  organizer?: { emailAddress?: { name?: string; address?: string } }
  attendees?: { emailAddress?: { name?: string; address?: string }; status?: { response?: string } }[]
  lastModifiedDateTime?: string
}

const GRAPH_RESPONSE_MAP: Record<string, Attendee['response']> = {
  accepted: 'accepted',
  declined: 'declined',
  tentativelyAccepted: 'tentative',
  notResponded: 'needs-action',
  none: 'needs-action',
}

function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Graph returns '2026-08-20T15:00:00.0000000' — already local given the Prefer header. */
function graphStamp(value: string): string {
  return value.slice(0, 16)
}

async function fetchMicrosoftCalendars(accountEmail: string): Promise<LinkedCalendar[]> {
  const response = await authedFetch(
    'microsoft',
    'https://graph.microsoft.com/v1.0/me/calendars?$select=id,name,hexColor,canEdit,isDefaultCalendar&$top=100',
  )
  const body = (await response.json()) as { value?: GraphCalendar[] }
  return (body.value ?? []).map((item, index) => ({
    id: `microsoft:${item.id}`,
    name: item.name || 'Calendar',
    provider: 'microsoft' as const,
    accountEmail,
    color: item.hexColor && item.hexColor.startsWith('#') ? item.hexColor : colorFor(item.id, index),
    enabled: true,
    readOnly: item.canEdit === false,
    memberId: null,
  }))
}

async function fetchMicrosoftEvents(
  calendars: LinkedCalendar[],
  daysBack: number,
  daysForward: number,
): Promise<PlannerEvent[]> {
  const { start, end } = windowBounds(daysBack, daysForward)
  const events: PlannerEvent[] = []
  const timeZone = localTimeZone()

  for (const calendar of calendars) {
    const providerId = calendar.id.slice('microsoft:'.length)
    const params = new URLSearchParams({
      startDateTime: start.toISOString(),
      endDateTime: end.toISOString(),
      $top: '250',
      $orderby: 'start/dateTime',
      $select: 'id,subject,bodyPreview,isAllDay,isCancelled,start,end,location,organizer,attendees,lastModifiedDateTime',
    })
    // calendarView expands recurring series; the Prefer header returns times in
    // the browser's zone so no client-side conversion is needed.
    const response = await authedFetch(
      'microsoft',
      `https://graph.microsoft.com/v1.0/me/calendars/${encodeURIComponent(providerId)}/calendarView?${params}`,
      { Prefer: `outlook.timezone="${timeZone}"` },
    )
    const body = (await response.json()) as { value?: GraphEvent[] }
    for (const item of body.value ?? []) {
      if (item.isCancelled) continue
      const startValue = item.start?.dateTime
      const endValue = item.end?.dateTime
      if (!startValue) continue
      const allDay = !!item.isAllDay
      events.push({
        id: `microsoft:${providerId}:${item.id}`,
        title: item.subject?.trim() || '(no title)',
        // All-day events come back as midnight-to-midnight; Graph's end is
        // exclusive, which is what eventDaySpan() expects.
        start: allDay ? startValue.slice(0, 10) : graphStamp(startValue),
        end: allDay
          ? (endValue ?? startValue).slice(0, 10)
          : graphStamp(endValue ?? startValue),
        allDay,
        location: item.location?.displayName || undefined,
        notes: item.bodyPreview || undefined,
        calendarId: calendar.id,
        origin: 'calendar',
        originRef: item.id,
        organizer: item.organizer?.emailAddress?.address || item.organizer?.emailAddress?.name,
        attendees: (item.attendees ?? []).map<Attendee>((a) => ({
          name: a.emailAddress?.name || a.emailAddress?.address || 'Guest',
          email: a.emailAddress?.address,
          response: a.status?.response ? GRAPH_RESPONSE_MAP[a.status.response] : undefined,
        })),
        memberIds: calendar.memberId ? [calendar.memberId] : [],
        category: 'other',
        updatedAt: item.lastModifiedDateTime ?? new Date().toISOString(),
      })
    }
  }
  return events
}

// ─── Public surface ──────────────────────────────────────────

export interface ProviderSnapshot {
  calendars: LinkedCalendar[]
  events: PlannerEvent[]
}

export async function fetchProviderCalendars(
  provider: OAuthProvider,
  accountEmail: string,
): Promise<LinkedCalendar[]> {
  return provider === 'google'
    ? fetchGoogleCalendars(accountEmail)
    : fetchMicrosoftCalendars(accountEmail)
}

/**
 * Pull the events for the calendars the owner has enabled. Read-only calendars
 * are included — a holiday or school feed is exactly the kind of thing that
 * should show up in the aggregate.
 */
export async function fetchProviderEvents(
  provider: OAuthProvider,
  calendars: LinkedCalendar[],
  daysBack = 30,
  daysForward = 120,
): Promise<PlannerEvent[]> {
  const active = calendars.filter((c) => c.enabled && c.provider === provider)
  if (active.length === 0) return []
  return provider === 'google'
    ? fetchGoogleEvents(active, daysBack, daysForward)
    : fetchMicrosoftEvents(active, daysBack, daysForward)
}
